import axios from 'axios';
import { Capacitor } from '@capacitor/core';

// API Base Configuration
// In production on HTTPS Firebase Hosting:
// We use '' (relative URL) so browser uses HTTPS same-origin and avoids Mixed Content errors.
// On Native Capacitor (Android/iOS), relative paths fail because origin is capacitor://localhost.
// So native builds fallback to production domain or process.env.REACT_APP_API_URL.
const DEFAULT_REMOTE_API = 'https://prmanager-f881b.web.app';
const isBrowser = typeof window !== 'undefined' && Boolean(window.location);
const isLocalhostBrowser = isBrowser && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

const API_BASE_URL = (() => {
  if (Capacitor.isNativePlatform()) {
    return process.env.REACT_APP_NATIVE_API_URL || DEFAULT_REMOTE_API;
  }
  if (isBrowser && !isLocalhostBrowser) {
    return '';
  }
  return process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:5005');
})()

if (process.env.NODE_ENV === 'development') {
  console.log('🔧 API_BASE_URL (Express Server):', API_BASE_URL);
}

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor for auth token and logging
api.interceptors.request.use(
  (config) => {
    // Add auth token if available
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // AI istekleri (LLM analizi, menü tarama, cevap iyileştirme) için timeout süresini 60 saniyeye çıkar
    if (config.url && (config.url.includes('/ai-assistant') || config.url.includes('/ai/'))) {
      config.timeout = 60000;
    }

    // Auth istekleri (giriş, oturum, cold start toleransı) için 35 saniye
    if (config.url && config.url.includes('/auth/')) {
      config.timeout = 35000;
    }

    const vpsBase = 'https://104-196-48-134.nip.io';

    // WhatsApp ve mesajlaşma istekleri için timeout süresini 35 saniyeye çıkar (socket handshake için)
    if (config.url && config.url.includes('/whatsapp')) {
      config.timeout = 35000;
      if (!config.headers) config.headers = {};
      config.headers['x-admin-secret'] = 'prmanager_secret_2026';
      
      // 🛡️ VPS Baileys Auth Token: VPS sunucusu sistem JWT_SECRET ile imzalı token bekler
      config.headers.Authorization = 'Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpZCI6InN5c3RlbV9hZG1pbiIsInJvbGUiOiJhZG1pbiJ9.ikzv2zeL-_UY9V4WaGi0OEsZS17hdMkWWTro1eB7HVA';
      
      // WhatsApp Baileys servisi 7/24 VPS üzerinde çalıştığı için her ortamda doğrudan VPS'e yönlendir
      if (!config.url.startsWith('http')) {
        config.url = `${vpsBase}${config.url.startsWith('/') ? '' : '/'}${config.url}`;
      } else if (config.url.includes('localhost:5005')) {
        config.url = config.url.replace(/https?:\/\/localhost:5005/, vpsBase);
      }
    }

    // 🚀 Platform Entegrasyonları (Trendyol, Yemeksepeti, Getir, POS terminal, Platform Ayarları) 
    // Bu servisler ve arka plan entegrasyonları VPS üzerinde 7/24 çalışır.
    const isPlatformRequest = config.url && (
      config.url.includes('/platform-orders') ||
      config.url.includes('/platform-settings') ||
      config.url.includes('/pos-terminal') ||
      config.url.includes('/pos-integrations') ||
      config.url.includes('/integrations/trendyol') ||
      config.url.includes('/integrations/getir') ||
      config.url.includes('/brands/virtual')
    );
    if (isPlatformRequest) {
      config.timeout = 45000;
      if (!config.url.startsWith('http')) {
        config.url = `${vpsBase}${config.url.startsWith('/') ? '' : '/'}${config.url}`;
      } else if (config.url.includes('localhost:5005')) {
        config.url = config.url.replace(/https?:\/\/localhost:5005/, vpsBase);
      }
    }
    
    if (process.env.NODE_ENV === 'development') {
      console.log(`🔄 API Request: ${config.method?.toUpperCase()} ${config.url}`);
    }
    return config;
  },
  (error) => {
    console.error('❌ API Request Error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor for error handling and token refresh
api.interceptors.response.use(
  (response) => {
    if (process.env.NODE_ENV === 'development') {
      console.log(`✅ API Response: ${response.status} ${response.config.url}`);
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    
    // Handle 401 Unauthorized errors (token expired/invalid)
    if (error.response?.status === 401 && !originalRequest._retry) {
      // Don't auto-clear if we are trying to login - it might just be wrong credentials
      const isLoginRequest = originalRequest.url?.includes('/auth/business-login') || 
                             originalRequest.url?.includes('/auth/finalize-selection');
      
      if (isLoginRequest) {
        return Promise.reject(error);
      }

      // Arka plan veya önemsiz polling / WhatsApp isteklerinde oturumu aniden öldürme
      const isBackgroundRequest = originalRequest.url?.includes('/status/') ||
                                  originalRequest.url?.includes('/notifications') ||
                                  originalRequest.url?.includes('/whatsapp') ||
                                  originalRequest.url?.includes('/ping');
      if (isBackgroundRequest) {
        return Promise.reject(error);
      }

      originalRequest._retry = true;
      console.warn('🔑 Token expired or unauthorized:', originalRequest.url);

      // 🛡️ SESSİZ TOKEN YENİLEME: Eğer Firebase Auth ile giriş yapılmışsa taze ID token alıp tekrar dene
      try {
        const { auth } = await import('../../services/firebase');
        if (auth && auth.currentUser) {
          const freshToken = await auth.currentUser.getIdToken(true);
          if (freshToken) {
            console.log('🔄 Firebase ID token refreshed successfully after 401');
            localStorage.setItem('token', freshToken);
            originalRequest.headers['Authorization'] = `Bearer ${freshToken}`;
            return api(originalRequest);
          }
        }
      } catch (refreshErr) {
        console.warn('⚠️ Silent token refresh attempt failed:', refreshErr?.message);
      }
      
      // Kullanıcının localStorage'da kayıtlı user bilgisi varsa oturumu hemen öldürme (offline/arka plan devamlılığı)
      const existingUser = localStorage.getItem('user');
      if (existingUser) {
        console.warn('🛡️ User exists in localStorage, suppressing aggressive auto-logout on 401');
        return Promise.reject(error);
      }

      // Token'ı geçersiz kıl ama offline/kurtarma için user profilini hemen silme
      localStorage.removeItem('token');
      
      // Trigger auth state change event
      window.dispatchEvent(new Event('authStateChanged'));
      
      // Redirect to login if not already there AND not on a public branded domain
      const isPublicDomain = window.location.hostname.includes('menu.') || 
                             window.location.hostname.includes('qr.') ||
                             window.location.pathname.startsWith('/menu/') ||
                             window.location.pathname.startsWith('/qr/');
      
      if (!window.location.pathname.includes('/login') && !isPublicDomain) {
        // Yumuşak yönlendirme
        window.location.href = '/login';
      }
      
      return Promise.reject(error);
    }
    
    // Handle network errors
    if (!error.response) {
      const isPolling = error.config?.url && error.config.url.includes('/status/');
      if (!isPolling && process.env.NODE_ENV === 'development') {
        console.warn('🌐 Network Error: Could not connect to server:', error.config?.url);
      }
      return Promise.reject(error);
    }
    
    console.error('❌ API Response Error:', error.response?.data || error.message);
    return Promise.reject(error);
  }
);

// Products API
export const productsAPI = {
  // Get all products with pagination and filters
  getAll: (params = {}) => {
    const { page = 1, limit = 20, category, search, sortBy } = params;
    return api.get('/api/products', { params: { page, limit, category, search, sortBy } });
  },

  // Get single product by ID
  getById: (id) => api.get(`/api/products/${id}`),

  // Search product by SKU, barcode or external code
  searchByCode: (code) => api.get(`/api/products/search/code/${code}`),

  // Create new product
  create: (productData) => api.post('/api/products', productData),

  // Update product
  update: (id, productData) => api.put(`/api/products/${id}`, productData),

  // Delete product
  delete: (id) => api.delete(`/api/products/${id}`),

  // Get product categories
  getCategories: () => api.get('/api/products/categories'),

  // Import products from XML
  importXML: (formData) => api.post('/api/products/import', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),

  // Bulk operations
  bulkUpdate: (operations) => api.post('/api/products/bulk', operations),
  bulkDelete: (productIds) => api.delete('/api/products/bulk', { data: { productIds } }),
};

// Brands API
export const brandsAPI = {
  getAll: (params = {}) => api.get('/api/brands', { params }),
  getById: (id) => api.get(`/api/brands/${id}`),
  create: (brandData) => api.post('/api/brands', brandData),
  update: (id, brandData) => api.put(`/api/brands/${id}`, brandData),
  delete: (id) => api.delete(`/api/brands/${id}`),
  getAnalytics: (id, period = 'month') => api.get(`/api/brands/${id}/analytics`, { params: { period } }),
};

// Orders API  
export const ordersAPI = {
  getAll: (params = {}) => api.get('/api/orders', { params }),
  getById: (id) => api.get(`/api/orders/${id}`),
  create: (orderData) => api.post('/api/orders', orderData),
  updateStatus: (id, statusData) => api.put(`/api/orders/${id}/status`, statusData),
  getAnalytics: (params = {}) => api.get('/api/orders/analytics/summary', { params }),
};

// Businesses API (Unified Business Management)
export const businessesAPI = {
  // Get all businesses for authenticated user
  getAll: (userId) => {
    const params = userId ? { userId } : {};
    return api.get('/api/businesses', { params });
  },
  
  // Get single business with full details
  getById: (id, userId = null) => {
    const params = userId ? { userId } : {};
    return api.get(`/api/businesses/${id}`, { params });
  },
  
  // Create new business with subscription
  create: (businessData) => api.post('/api/businesses', businessData),
  
  // Update business information
  update: (id, businessData) => api.put(`/api/businesses/${id}`, businessData),
  
  // Update business settings (RestaurantSettings integration)
  updateSettings: (id, settings) => api.put(`/api/businesses/${id}/settings`, { settings }),
  
  // Delete business
  delete: (id) => api.delete(`/api/businesses/${id}`),
  
  // Get business analytics
  getAnalytics: (id, params = {}) => api.get(`/api/businesses/${id}/analytics`, { params }),
  
  // Business subscription management
  upgradeSubscription: (id, subscriptionData) => api.put(`/api/businesses/${id}/subscription`, subscriptionData),
  addModule: (id, moduleData) => api.post(`/api/businesses/${id}/modules`, moduleData),
  removeModule: (id, moduleId) => api.delete(`/api/businesses/${id}/modules/${moduleId}`)
};

// Ads API
export const adsAPI = {
  getAll: (params = {}) => api.get('/api/ads', { params }),
  getById: (id) => api.get(`/api/ads/${id}`),
  create: (adData) => api.post('/api/ads', adData),
  update: (id, adData) => api.put(`/api/ads/${id}`, adData),
  delete: (id) => api.delete(`/api/ads/${id}`),
};

export default api;
